import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material'
import { FC, ReactNode } from 'react'
import { tokens } from '@/theme'

export interface KpiCardProps {
  /** Rótulo curto acima do valor; renderizado em caixa alta ("VOLUME EM ANÁLISE"). */
  label: string
  /** Número/valor principal (26px, peso 700 — Card KPI do Figma). */
  valor: ReactNode
  /** Linha de apoio abaixo do valor ("+3 esta semana"). */
  legenda?: ReactNode
  /**
   * `escura` = fundo Ink com texto claro; `marca` = fundo laranja Originei com texto
   * branco (11/09 — o card do dinheiro). Por convenção da PÁGINA o primeiro card da
   * linha é o escuro — a decisão é de quem monta a linha, não deste componente.
   */
  variante?: 'clara' | 'escura' | 'marca'
  /** Substitui valor e legenda por skeletons, mantendo o rótulo e a altura. */
  carregando?: boolean
}

interface Paleta {
  fundo: string
  borda: string
  label: string
  valor: string
  legenda: string
  skeleton?: string
  bolha: string
}

const PALETA: Record<NonNullable<KpiCardProps['variante']>, Paleta> = {
  clara: {
    fundo: tokens.surfaceCard,
    borda: tokens.divider,
    label: tokens.textSecondary,
    valor: tokens.brandInk,
    legenda: tokens.textSecondary,
    bolha: tokens.brandPrimarySoft,
  },
  escura: {
    fundo: tokens.brandInk,
    borda: tokens.brandInk,
    label: 'rgba(255,255,255,0.64)',
    valor: '#FFFFFF',
    legenda: 'rgba(255,255,255,0.72)',
    skeleton: 'rgba(255,255,255,0.16)',
    bolha: 'rgba(255,255,255,0.06)',
  },
  marca: {
    fundo: tokens.brandPrimary,
    borda: tokens.brandPrimary,
    label: 'rgba(255,255,255,0.8)',
    valor: '#FFFFFF',
    legenda: 'rgba(255,255,255,0.88)',
    skeleton: 'rgba(255,255,255,0.24)',
    bolha: 'rgba(255,255,255,0.14)',
  },
}

/**
 * Card KPI do Figma (Foundations 4:9): label uppercase · valor 26/700 · legenda, com a
 * bolha decorativa no canto (identidade, 11/09). Todas as páginas consomem este
 * componente — não duplicar em `pages/`.
 */
export const KpiCard: FC<KpiCardProps> = ({
  label,
  valor,
  legenda,
  variante = 'clara',
  carregando = false,
}) => {
  const p = PALETA[variante]
  return (
    <Card
      data-testid="kpi-card"
      data-variante={variante}
      sx={{
        backgroundColor: p.fundo,
        borderColor: p.borda,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: -28,
          bottom: -36,
          width: 112,
          height: 112,
          borderRadius: '50%',
          backgroundColor: p.bolha,
          pointerEvents: 'none',
        }}
      />
      <CardContent
        sx={{ padding: 2.5, '&:last-child': { paddingBottom: 2.5 }, position: 'relative' }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: '0.06em',
            color: p.label,
          }}
        >
          {label}
        </Typography>

        {carregando ? (
          <Skeleton width="60%" height={38} sx={{ bgcolor: p.skeleton }} />
        ) : (
          <Typography
            component="div"
            sx={{
              fontSize: '1.625rem',
              fontWeight: 700,
              lineHeight: 1.25,
              letterSpacing: '-0.02em',
              marginTop: 0.5,
              color: p.valor,
            }}
          >
            {valor}
          </Typography>
        )}

        {carregando ? (
          <Skeleton width="40%" sx={{ bgcolor: p.skeleton }} />
        ) : (
          legenda !== undefined && (
            <Typography variant="caption" component="div" sx={{ marginTop: 0.5, color: p.legenda }}>
              {legenda}
            </Typography>
          )
        )}
      </CardContent>
    </Card>
  )
}

export default KpiCard
